// src/pages/Alerts.js
import React, { useState, useEffect } from 'react';
import { fetchAlerts, createAlert, updateAlert, deleteAlert } from '../services/alertService';
import { useAuth } from '../context/AuthContext';
import LoadingScreen from '../components/LoadingScreen';
import { useOAuth2Monitor } from '../hooks/useOAuth2Monitor';
import { Alert, Box } from '@mui/material';

function Alerts() {
  const { userData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [newAlert, setNewAlert] = useState({ type: '', threshold: '' });
  const [creating, setCreating] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  
  // Monitoreo OAuth2 para alertas
  const { 
    metrics: oauth2Metrics, 
    isCritical, 
    hasWarnings, 
    getRecentErrors 
  } = useOAuth2Monitor();
  
  // Logging de errores OAuth2 para debugging
  useEffect(() => {
    if (hasWarnings()) {
      console.warn('[Alerts] OAuth2 Warnings detected:', {
        systemHealth: oauth2Metrics.systemHealth,
        errorRate: oauth2Metrics.errorRate
      });
    }
    
    if (isCritical()) {
      console.error('[Alerts] OAuth2 Critical state detected:', {
        systemHealth: oauth2Metrics.systemHealth,
        consecutiveErrors: oauth2Metrics.consecutiveErrors
      });
    }
  }, [hasWarnings, isCritical, oauth2Metrics]);

  useEffect(() => {
    const fetchAlertsData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Obtener datos con manejo de errores integrado
        const response = await fetchAlerts();
        
        // Procesamiento defensivo con diagnóstico
        let processedAlerts = [];
        
        try {
          // Analizar estructura de la respuesta
          const alertsData = response?.data;
          const isDataArray = Array.isArray(alertsData);
          const hasAlertsProperty = alertsData && 
                                typeof alertsData === 'object' && 
                                Array.isArray(alertsData.alerts);
          const isHTML = typeof alertsData === 'string' && 
                      (alertsData.includes('<!DOCTYPE') || alertsData.includes('<html'));
          
          // Registrar diagnóstico de respuesta
          localStorage.setItem('nubelytics_alerts_response', JSON.stringify({
            type: typeof alertsData,
            isArray: isDataArray,
            hasAlertsProperty: hasAlertsProperty,
            isHTML: isHTML,
            timestamp: new Date().toISOString(),
            sample: isHTML ? alertsData.substring(0, 50) + '...' : null,
          }));
          
          // Normalizar datos según estructura
          if (isDataArray) {
            processedAlerts = alertsData;
          } else if (hasAlertsProperty) {
            processedAlerts = alertsData.alerts;
          }
          
          console.log('[Alerts] Datos procesados:', {
            original: typeof alertsData === 'string' ? alertsData.substring(0, 100) + '...' : alertsData,
            processed: processedAlerts,
            isArray: Array.isArray(processedAlerts)
          });
        } catch (e) {
          console.error('[Alerts] Error procesando datos:', e);
        }
        
        // Garantizar array válido
        setAlerts(Array.isArray(processedAlerts) ? processedAlerts : []);
      } catch (err) {
        console.error('[Alerts] Error cargando alertas:', err);
        setError('No se pudieron cargar las alertas.');
      } finally {
        setLoading(false);
      }
    };
    
    if (userData) fetchAlertsData();
  }, [userData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAlert(prev => ({ ...prev, [name]: value }));
  };

  const handleToggleAlert = async (alert) => {
    setUpdatingId(alert.id);
    try {
      await updateAlert(alert.id, { active: !alert.active });
      const resp = await fetchAlerts();
      // Normalizar respuesta para asegurar array de alertas
      const fetched = resp?.data;
      const normalizedAlerts = Array.isArray(fetched)
        ? fetched
        : Array.isArray(fetched?.alerts)
          ? fetched.alerts
          : [];
      setAlerts(normalizedAlerts);
    } catch (err) {
      setError('No se pudo actualizar la alerta.');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDeleteAlert = async (alert) => {
    if (!window.confirm('¿Estás seguro de eliminar esta alerta?')) return;
    setDeletingId(alert.id);
    try {
      await deleteAlert(alert.id);
      setAlerts(prev => prev.filter(a => a.id !== alert.id));
    } catch (err) {
      setError('No se pudo eliminar la alerta.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCreateAlert = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      await createAlert(newAlert);
      setNewAlert({ type: '', threshold: '' });
      // Recargar alertas
      const response = await fetchAlerts();
      // Normalizar respuesta como en carga inicial
      const fetched = response?.data;
      const normalizedAlerts = Array.isArray(fetched)
        ? fetched
        : Array.isArray(fetched?.alerts)
          ? fetched.alerts
          : [];
      setAlerts(normalizedAlerts);
    } catch (err) {
      setError('No se pudo crear la alerta.');
    } finally {
      setCreating(false);
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <Box sx={{ p: 3 }}>
      {/* Alertas OAuth2 */}
      {isCritical() && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>Sistema de autenticación crítico:</strong> Se detectaron múltiples errores de OAuth2.
        </Alert>
      )}
      
      {hasWarnings() && !isCritical() && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Advertencia OAuth2:</strong> Se detectaron algunos errores de autenticación.
        </Alert>
      )}
      
      <div className="alerts-page">
      <h1>Alertas</h1>
      {error && <div className="alert error">{error}</div>}
      <form className="new-alert-form" onSubmit={handleCreateAlert}>
        <select name="type" value={newAlert.type} onChange={handleInputChange} required>
          <option value="">Tipo de alerta</option>
          <option value="high_abandoned">Carritos abandonados altos</option>
          <option value="low_sales">Bajas ventas</option>
          <option value="high_orders">Órdenes altas</option>
        </select>
        <input
          type="number"
          name="threshold"
          placeholder="Umbral"
          value={newAlert.threshold}
          onChange={handleInputChange}
          required
        />
        <button type="submit" disabled={creating}>Crear alerta</button>
      </form>
      <div className="alerts-list">
        <table>
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Umbral</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {alerts.length === 0 ? (
              <tr><td colSpan="4">No tienes alertas configuradas.</td></tr>
            ) : (
              alerts.map((alert) => (
                <tr key={alert.id || alert._id}>
                  <td>{alert.type}</td>
                  <td>{alert.threshold}</td>
                  <td>
                    <button
                      className="toggle-btn"
                      onClick={() => handleToggleAlert(alert)}
                      disabled={updatingId === alert.id}
                    >
                      {alert.active ? 'Desactivar' : 'Activar'}
                    </button>
                  </td>
                  <td>
                    <button
                      className="delete-btn"
                      onClick={() => handleDeleteAlert(alert)}
                      disabled={deletingId === alert.id}
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <style jsx="true">{`
        .alerts-page { max-width: 700px; margin: 0 auto; }
        .new-alert-form { display: flex; gap: 10px; margin-bottom: 20px; }
        .new-alert-form select, .new-alert-form input {
          padding: 8px; border-radius: 4px; border: 1px solid #ced4da;
        }
        .new-alert-form button {
          background: #3498db; color: white; border: none; border-radius: 4px; padding: 8px 16px; cursor: pointer;
        }
        .alerts-list { background: #fff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); overflow-x: auto; }
         .toggle-btn, .delete-btn { padding: 6px 12px; border: none; border-radius: 4px; cursor: pointer; }
         .toggle-btn { background: #17a2b8; color: #fff; }
         .delete-btn { background: #dc3545; color: #fff; }
        table { width: 100%; border-collapse: collapse; }
        th, td { padding: 10px 15px; border-bottom: 1px solid #e9ecef; }
        th { background: #f8f9fa; text-align: left; }
        .alert.error { background: #f8d7da; color: #721c24; padding: 10px; border-radius: 4px; margin-bottom: 20px; }
      `}</style>
      </div>
    </Box>
  );
}

export default Alerts;
